import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCircle, Phone, SignOut, Pencil, CheckCircle, ShieldCheck, HouseLine, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";
import { passengerApi, getPassengerUser, clearPassengerSession, setPassengerSession } from "@/lib/passengerApi";
import { MajheHeader, MajheBrand } from "@/components/majhe/MajheLayout";

export default function Profile() {
  const navigate = useNavigate();
  const stored = getPassengerUser();
  const [user, setUser] = useState(stored);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(stored?.name || "");
  const [counts, setCounts] = useState({ tickets: 0, cards: 0, passes: 0 });

  useEffect(() => {
    Promise.all([
      passengerApi.get("/me/tickets").then(r => r.data.length),
      passengerApi.get("/me/cards").then(r => r.data.length),
      passengerApi.get("/me/passes").then(r => r.data.length),
    ]).then(([t, c, p]) => setCounts({ tickets: t, cards: c, passes: p }));
  }, []);

  const saveName = async () => {
    if (!name.trim()) return;
    try {
      await passengerApi.patch("/me", { name });
      const updated = { ...user, name };
      setPassengerSession(localStorage.getItem("majhektcl_token"), updated);
      setUser(updated);
      setEditing(false);
      toast.success("Name updated");
    } catch (e) { toast.error("Failed"); }
  };

  const logout = () => {
    clearPassengerSession();
    toast.success("Signed out");
    navigate("/majhe/login", { replace: true });
  };

  return (
    <div data-testid="majhe-profile">
      <MajheHeader title="My Profile" onBack={() => navigate("/majhe")}/>

      <div className="px-5 pt-1">
        <div className="majhe-card">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
              <UserCircle size={36} weight="fill"/>
            </div>
            <div className="flex-1 min-w-0">
              {!editing ? (
                <>
                  <div className="text-lg font-extrabold truncate">{user?.name || "Add your name"}</div>
                  <div className="text-sm opacity-90 mt-0.5"><Phone size={12} weight="bold" className="inline mr-1"/>+91 {user?.mobile}</div>
                </>
              ) : (
                <input className="w-full bg-white/15 border border-white/20 rounded-lg px-3 py-2 text-white"
                  data-testid="profile-name-input"
                  value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"/>
              )}
            </div>
            {!editing ? (
              <button data-testid="profile-edit-btn" onClick={() => setEditing(true)}
                type="button" aria-label="Edit name"
                className="majhe-card-btn">
                <Pencil size={12} weight="bold" aria-hidden="true"/> Edit
              </button>
            ) : (
              <button data-testid="profile-save-btn" onClick={saveName}
                type="button" aria-label="Save name"
                className="majhe-card-btn">
                <CheckCircle size={12} weight="fill" aria-hidden="true"/> Save
              </button>
            )}
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <PStat label="Tickets" value={counts.tickets}/>
            <PStat label="Cards" value={counts.cards}/>
            <PStat label="Passes" value={counts.passes}/>
          </div>
        </div>

        <div className="mt-5">
          <Row tid="profile-row-tickets" icon={<HouseLine size={20} weight="bold"/>}
            title="Booking history" sub="All bookings ever made"
            onClick={() => navigate("/majhe/tickets")}/>
          <Row tid="profile-row-help" icon={<ShieldCheck size={20} weight="bold"/>}
            title="Help & Support" sub="0832-2438034 · 9 AM – 8 PM" />
          <Row tid="profile-row-about" icon={<Globe size={20} weight="bold"/>}
            title="About MajheKTCL" sub="A Kadamba Transport initiative" />
        </div>

        <button onClick={logout} data-testid="profile-logout"
          type="button" aria-label="Sign out of MajheApp"
          className="majhe-btn mt-6 mb-8"
          style={{ background: "#FEE2E2", color: "#991B1B", boxShadow: "none" }}>
          <SignOut size={18} weight="bold" aria-hidden="true"/> Sign out
        </button>

        <div className="text-center pb-6">
          <MajheBrand/>
          <div className="text-[11px] text-[#7B7390] mt-2">© 2026 Kadamba Transport Corporation Limited</div>
        </div>
      </div>
    </div>
  );
}

function PStat({ label, value }) {
  return (
    <div className="rounded-xl bg-white/12 border border-white/15 p-3 text-center">
      <div className="text-xl font-extrabold">{value}</div>
      <div className="text-[10px] opacity-85 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function Row({ icon, title, sub, onClick, tid }) {
  return (
    <button data-testid={tid} onClick={onClick}
      className="w-full flex items-center gap-3 bg-white rounded-2xl border border-[#EDE8FA] p-4 mb-2.5 text-left">
      <div className="w-11 h-11 rounded-xl bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-[#18142A]">{title}</div>
        <div className="text-[11px] text-[#7B7390]">{sub}</div>
      </div>
    </button>
  );
}
